import { cloneElement, isValidElement, useId, type ReactElement } from 'react';

import { cn } from '@/lib/cn';

interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * Accessible label + input wrapper.
 *
 * BUILD_STANDARDS §17 blocking a11y gate: label linked to input via
 * htmlFor/id, error/hint linked via aria-describedby, aria-invalid on
 * error state, aria-required for required. Every form in the app renders
 * inputs through this — if the a11y wiring regresses here, the whole app
 * regresses at once (and one test catches it).
 */
export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
}: FieldProps): JSX.Element {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  let child: React.ReactNode = children;
  if (isValidElement(children)) {
    const el = children as ReactElement<Record<string, unknown>>;
    child = cloneElement(el, {
      id: (el.props.id as string | undefined) ?? id,
      'aria-invalid': error ? true : undefined,
      'aria-describedby': describedBy,
      'aria-required': required || undefined,
    });
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      <label
        htmlFor={id}
        className="block text-xs font-medium text-mb-text uppercase tracking-wider"
      >
        {label}
        {required && (
          <>
            <span className="text-mb-danger ml-1" aria-hidden="true">
              *
            </span>
            <span className="sr-only"> (required)</span>
          </>
        )}
      </label>
      {child}
      {error ? (
        <p id={errorId} role="alert" className="text-xs text-mb-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-xs text-mb-text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
